import { browserUtil, tokenService } from '../../common'
import { authDao } from '../../dao/auth/auth-dao'
import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { logger } from 'src/util/logger'

// UI Components
const CenteredContainer: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      flexDirection: 'column',
    }}
  >
    {children}
  </div>
)

const LoadingView: React.FC = () => (
  <CenteredContainer>
    <h2>Authenticating...</h2>
    <p>Please wait while we complete your sign-in.</p>
  </CenteredContainer>
)

const ErrorView: React.FC<{ error: string; onReturnToLogin: () => void }> = ({ error, onReturnToLogin }) => (
  <CenteredContainer>
    <h2>Authentication Error</h2>
    <p>{error}</p>
    <button onClick={onReturnToLogin}>Return to Login</button>
  </CenteredContainer>
)

const SuccessView: React.FC = () => (
  <CenteredContainer>
    <h2>Authentication Successful</h2>
    <a href={window.location.origin} style={{ marginTop: '20px' }}>
      <button style={{ padding: '10px 20px', fontSize: '16px' }}>SUCCESS</button>
    </a>
  </CenteredContainer>
)

// Authentication status enum
enum AuthStatus {
  PROCESSING = 'PROCESSING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

/**
 * Screen that handles the OAuth callback flow by extracting access and refresh tokens
 * from URL query parameters and storing them using the tokenService.
 *
 * This screen is rendered at the /auth/callback route to handle token extraction
 * after OAuth redirects (like Apple Sign In).
 */
export const AuthCallbackScreen: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [status, setStatus] = useState<AuthStatus>(AuthStatus.PROCESSING)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Log component mount for debugging
  useEffect(() => {
    logger.info('AuthCallbackScreen mounted', {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      fullUrl: window.location.href,
    })
  }, [])

  // Handle authentication process
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const code = searchParams.get('code')

    logger.info('AuthCallbackScreen processing', {
      code,
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      fullUrl: window.location.href,
    })

    if (!code) {
      logger.warn('No authentication code found in URL')
      setErrorMessage('No authentication code found in URL')
      setStatus(AuthStatus.ERROR)
      return
    }

    // Exchange the temporary code for tokens (use authDao so base URL respects REACT_APP_API_BASE_URL)
    authDao
      .exchangeTempCode({ code })
      .then(({ accessToken, refreshToken, redirectUrl }) => {
        tokenService.store({ accessToken, refreshToken })

        // // Remove code from URL
        // searchParams.delete('code')

        // Wait a moment to ensure the session context has updated
        if (redirectUrl) {
          browserUtil.redirect(redirectUrl)
        } else {
          setTimeout(() => {
            setStatus(AuthStatus.SUCCESS)
          }, 500)
        }
      })
      .catch((_error) => {
        logger.error('Auth callback error', { error: _error })
        setErrorMessage('Authentication failed. Please try again.')
        setStatus(AuthStatus.ERROR)

        // Clean up the URL even if the exchange failed
        // searchParams.delete('code')
        navigate({ pathname: location.pathname, search: searchParams.toString() }, { replace: true })
      })
  }, [location, navigate])

  // Render appropriate view based on status
  switch (status) {
    case AuthStatus.PROCESSING:
      return <LoadingView />
    case AuthStatus.ERROR:
      return <ErrorView error={errorMessage || 'Unknown error'} onReturnToLogin={() => navigate('/auth/get-in')} />
    case AuthStatus.SUCCESS:
      return <SuccessView />
    default:
      return <ErrorView error="Unexpected state" onReturnToLogin={() => navigate('/auth/get-in')} />
  }
}
